import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import Icon from 'dpl/common/components/Icon';
import SectionViewTracker from 'dpl/common/components/SectionViewTracker';
import BreederProfileIntroSectionSkeletonV2 from 'dpl/components/BreederProfile/Show/skeletons/BreederProfileIntroSectionSkeletonV2';
import SmartImage from 'dpl/components/SmartImage';
import { BREED_ID } from 'dpl/constants/query_params';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { aboutBreederResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';

import TruncatedText from './about/TruncatedText';

export default function BreederProfileIntroSection() {
  const breedId = useSelector(({ queryParams }: any) => queryParams[BREED_ID]);

  const {
    breederFirstName,
    analyticsPageName,
    breederProfileId,
    introParagraph
  } = useSelector(({ server }: any) => server);

  const {
    isFetching,
    value: {
      data: {
        intro_photo_url: introPhotoUrl,
        achievements: fetchedAchievements,
        questions
      }
    }
  } = useResourceQuery(
    aboutBreederResourceDefinition(breederProfileId, { breed_id: breedId })
  );

  const achievements = useMemo(() => {
    // If the only achievement is the member tenure, don't render the section
    if (
      fetchedAchievements.length === 1 &&
      fetchedAchievements[0].id === 'member-tenure'
    ) {
      return [];
    }

    return fetchedAchievements;
  }, [isFetching, fetchedAchievements]);

  if (isFetching) {
    return <BreederProfileIntroSectionSkeletonV2 className="nt6 nh6" />;
  }

  return (
    <SectionViewTracker
      name={analyticsPageName}
      category="breeder-profile-tab-about-meet-breeder"
      properties={{ breeder_profile_id: breederProfileId }}
    >
      <div className="BreederProfileIntroSection container-xxl pb8">
        {introPhotoUrl && (
          <div className="nt6 nh6">
            <div className="aspect-ratio aspect-ratio--6x5 relative mb8">
              <div className="aspect-ratio--object">
                <SmartImage
                  Tag="div"
                  className="w-100 h-100 cover bg-center"
                  loadingClass="bg-loading-animation"
                  wrapperOnlyClassName="overflow-hidden"
                  src={introPhotoUrl}
                />
              </div>
            </div>
          </div>
        )}
        <h2 className="font-28 md:font-40 lh-title mb3">
          Meet {breederFirstName}
        </h2>
        <TruncatedText
          className="font-16 secondary"
          text={introParagraph}
          lineHeight={1.4}
          maxLines={5}
        />
        {!isEmpty(achievements) && (
          <ul className="flex flex-column gap-24 bg-sand pv8 ph5 mt8 nh6">
            {achievements.map((achievement: any) => (
              <li key={achievement.id}>
                <div className="flex gap-12">
                  <Icon
                    name={achievement.icon_name}
                    className="secondary"
                    height="32px"
                    width="32px"
                  />
                  <div>
                    <p className="font-16 fw-medium lh-title mb1">
                      {achievement.title}
                    </p>
                    <p className="font-14 secondary">
                      {achievement.description}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {!isEmpty(questions) && (
          <div className="mt8">
            <h3 className="font-serif font-24 lh-title mb4">
              Q. & A. with {breederFirstName}
            </h3>
            <ul className="flex flex-column gap-24">
              {questions.map(({ question, answer }: any, index: number) => (
                <li key={index}>
                  <div>
                    <p className="font-16 fw-medium lh-title mb3">{question}</p>
                    <TruncatedText
                      className="font-16 secondary"
                      text={answer}
                      lineHeight={1.4}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </SectionViewTracker>
  );
}
